import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import LinkOutlined from '@material-ui/icons/LinkOutlined';
import MoreHorizOutlined from '@material-ui/icons/MoreHorizOutlined';
import * as React from 'react';
import { toast } from 'react-toastify';
import { CHECKOUT_ELEMENTS_URL } from 'settings';
import { BaseOfferTag } from 'v2/components/Shared/BaseOfferTag';
import { Space } from 'v2/components/Shared/Space';
import Truncate from 'v2/components/Shared/TruncateText';
import { useProductOffers } from 'v2/hooks/useProductOffers';

import type { ProductOffer } from 'v2/interfaces/product';

import { useStyles } from './styles';

type ListProps = {
  productId: number;
  handleActionsClick: (event: React.MouseEvent<HTMLButtonElement>, offer: ProductOffer) => void;
};

function TableState({ message }: { message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={6} align='center' style={{ padding: 16 }}>
        {message}
      </TableCell>
    </TableRow>
  );
}

function Component({ productId, handleActionsClick }: ListProps) {
  const classes = useStyles();
  const offersQuery = useProductOffers(
    {
      productId,
    },
    { enabled: !!productId }
  );

  function getBaseOffer(offer: ProductOffer) {
    return offer.prices?.find((price) => price.currency.code === 'BRL');
  }

  function formatPriceBasedOnBaseOffer(offer: ProductOffer) {
    const baseOffer = getBaseOffer(offer);
    if (!baseOffer) return 'R$ 0,00';
    return baseOffer.value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  function mountOfferLink(offer: ProductOffer) {
    return `${CHECKOUT_ELEMENTS_URL}/${offer.key}`;
  }

  function copyLinkToClipboard(link: string) {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado para a área de transferência');
  }

  return (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableCell>Nome da oferta</TableCell>
          <TableCell width={112}>Valor base</TableCell>
          <TableCell width={60} align='center'>
            Preços
          </TableCell>
          <TableCell>Página de pagamento (Checkout Sun)</TableCell>
          <TableCell align='center'>Visível para afiliados</TableCell>
          <TableCell width={75} align='center'>
            Ações
          </TableCell>
        </TableHead>
        <TableBody>
          {offersQuery.isLoading && <TableState message='Carregando...' />}
          {!offersQuery.isLoading && !offersQuery.data?.length && !offersQuery.isError && <TableState message='Nenhuma oferta cadastrada' />}
          {offersQuery.isError && <TableState message='Ocorreu um erro ao carregar as ofertas' />}
          {offersQuery.data?.map((offer, index) => (
            <TableRow key={offer.id}>
              <TableCell
                style={{
                  whiteSpace: 'nowrap',
                }}
              >
                <Truncate count={20} style={{ display: 'inline-block' }}>
                  {offer.name}
                </Truncate>
                {offer.is_default && (
                  <>
                    <Space
                      axis='horizontal'
                      size={8}
                      style={{
                        display: 'inline-block',
                      }}
                    />
                    <BaseOfferTag />
                  </>
                )}
              </TableCell>
              <TableCell>{formatPriceBasedOnBaseOffer(offer)}</TableCell>
              <TableCell align='center'>{String(offer.prices?.length || 0).padStart(2, '0')}</TableCell>
              <TableCell>
                <div className={classes.checkoutLinkContainer}>
                  <LinkOutlined style={{ fontSize: 18 }} />
                  <a href={mountOfferLink(offer)} target='_blank' rel='noreferrer'>
                    {mountOfferLink(offer)}
                  </a>
                  <IconButton id={`offer-copy-link-${index}`} size='small' onClick={() => copyLinkToClipboard(mountOfferLink(offer))}>
                    <FileCopyOutlined style={{ fontSize: 18 }} />
                  </IconButton>
                </div>
              </TableCell>
              <TableCell align='center'>{offer.is_visible_affiliate ? 'Sim' : 'Não'}</TableCell>
              <TableCell align='center'>
                <IconButton id={`offer-actions-${index}`} size='small' onClick={(e) => handleActionsClick(e, offer)}>
                  <MoreHorizOutlined />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const List = React.memo(Component);

export { List };
